import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStateService, LocalizationService, SubscriptionService } from '@abp/ng.core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  ClassificationTypeService,
  CompanySiteDto,
  DocRole,
  DwActiveLookupRequestDto,
} from '@digiwall/goliath-core/proxy';
import { inject, Injector } from '@angular/core';
import {
  AddressDto,
  SupplierDto,
  SupplierService,
  SalesPersonService,
} from '@digiwall/goliath-contacts/proxy';
import { BehaviorSubject, map, Observable, takeUntil } from 'rxjs';
import { AddressRole } from '@digiwall/goliath-contacts';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogBoxCloneComponent } from '../dialog-box-clone/dialog-box-clone.component';
import {
  CreateUpdateSupplierDocDto,
  DayBookService,
  SupplierDocDefaultValueDto,
  SupplierDocDto,
} from '@digiwall/goliath-documents/proxy';
import { DocTemplateService } from '../base-docs/detail/doc-template-selection/doc-template.service';
import { CompanySiteStore, CurrencyStore } from '@digiwall/goliath-core';
import { toObservable } from '@angular/core/rxjs-interop';
import { conditionalValidator, DeactivatableComponentService } from '@digiwall/core';
import {
  BaseThirdPartyDocDetail,
  IThirdPartyDocService,
  IThirdPartyService,
  FieldMapping,
} from '../base-third-party-docs/base-third-party-doc-detail';
import { SupplierDeliveryMode } from './supplier-delivery-mode.enum';

export interface IBaseSupplierDocService<
  TDocDto extends SupplierDocDto,
  TCreateUpdateDto extends CreateUpdateSupplierDocDto,
  TDefaultValueDto extends SupplierDocDefaultValueDto,
> extends IThirdPartyDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto> {}

export abstract class BaseSupplierDocDetail<
  TDocService extends IBaseSupplierDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto>,
  TDocDto extends SupplierDocDto,
  TCreateUpdateDto extends CreateUpdateSupplierDocDto,
  TDefaultValueDto extends SupplierDocDefaultValueDto,
> extends BaseThirdPartyDocDetail<
  TDocService,
  TDocDto,
  TCreateUpdateDto,
  TDefaultValueDto,
  IThirdPartyService
> {
  deliveryAddressSelected$ = new BehaviorSubject<AddressDto>(null);

  showSearch: boolean;

  deliveryMode = SupplierDeliveryMode;
  deliveryAddress$: Observable<AddressDto[]>;

  thirdPartyAddress: AddressDto;
  thirdPartyDirectDeliveryAddress$: Observable<AddressDto[]>;
  thirdPartyPickUpAddress$: Observable<AddressDto[]>;

  companySite$: Observable<CompanySiteDto[]>;
  companySites: CompanySiteDto[];

  companySiteStore = inject(CompanySiteStore);

  protected supplierService = inject(SupplierService);
  protected salesPersonService = inject(SalesPersonService);
  protected modalService = inject(NgbModal);
  protected currencyStore = inject(CurrencyStore);

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    service: TDocService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    quoteTemplateService: DocTemplateService,
    injector: Injector,
    thirdPartyService: SupplierService,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      service,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      quoteTemplateService,
      injector,
      config,
      confirmationService,
      deactivableComponentService,
      thirdPartyService,
    );

    this.thirdPartyId$.subscribe(thirdPartyId => {
      if (thirdPartyId != null) {
        this.subscriptionService.addOne(
          this.supplierService.get(thirdPartyId),
          (response: SupplierDto) => {
            let currencyControl = this.form.get('currencyId');
            if (currencyControl?.value != response.currencyId) {
              currencyControl.setValue(response.currencyId);
            }
          },
        );
      } else {
        this.customerAddress = null;
      }
    });

    this.companySiteStore.loading();
    if (this.companySiteStore.loaded) {
      this.companySite$ = toObservable(this.companySiteStore.entities);
    }
  }

  override get formModel() {
    let formModel = super.formModel;

    formModel = Object.assign({}, super.formModel, {
      deliveryAddress: this.formBuilder.group({
        thirdPartyAddressId: [null],
        street: [null],
        localityId: [null],
        countryId: [null],
        latitude: [null],
        longitude: [null],
        address2: [null],
        contactInfo: [null],
      }),

      currencyCode: [null, Validators.required],
      currencyRate: [null, [Validators.min(0), Validators.max(1)]],
      representativeId: [null],
      representativeCommision: [null],
      deliveryAddressIdSelected: [null],
      supplierDocOriginId: [null],
      supplierDeliveryModeId: [
        null,
        conditionalValidator(
          () =>
            this.docRole != DocRole.SupplierDeliveryNote &&
            this.docRole != DocRole.SupplierReturnNote,
          Validators.required,
        ),
      ],
    });

    return formModel;
  }

  protected salesPersonSelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'lastName' }, { property: 'firstName' }];
    dto.queryProperties = ['code', 'lastName', 'firstName'];
    return this.salesPersonService.getLookupList(dto);
  };

  protected override initFormObservers() {
    super.initFormObservers();

    this.form
      .get('companySiteId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(companySiteId => {
        this.setDeliveryAddress();
      });

    this.form
      .get('supplierDeliveryModeId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(deliveryModeId => {
        this.form.get('deliveryAddressIdSelected').setValue(null);
        this.setEmptyAddressComponent();
        this.setDeliveryAddress();
      });

    this.form
      .get('deliveryAddressIdSelected')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(deliveryAddressIdSelected => {
        this.setEmptyAddressComponent();
        this.setDeliveryAddress();
      });

    this.form
      .get('thirdPartyId')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(thirdPartyId => {
        if (thirdPartyId != null) {
          this.subscriptionService.addOne(
            this.supplierService.getSupplierMainAddress(thirdPartyId),
            (response: AddressDto) => {
              this.thirdPartyAddress = response;
            },
          );
        }
      });

    this.form
      .get('currencyCode')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(currencyCode => {
        if (currencyCode != null) {
          this.setCurrencyRate(currencyCode);
        }
      });
  }

  protected setCurrencyRate(currencyCode: string) {
    this.subscriptionService.addOne(
      this.currencyService.getCurrentCurrencyRate(
        this.form.get('documentDate').value,
        currencyCode,
      ),
      response => {
        this.form.get('currencyRate').setValue(response);
      },
    );
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    super.onThirdPartyChanged(thirdPartyId);
    this.thirdPartyId$.next(thirdPartyId);

    if (thirdPartyId != null) {
      this.loadAddresses(thirdPartyId);
    }
  };

  protected override getFieldMappings(): FieldMapping[] {
    return [
      ...super.getFieldMappings(),
      {
        formField: 'currencyCode',
        customerField: 'currency' as any,
        transform: (currency: any) => currency?.code,
      },
      { formField: 'supplierDeliveryModeId', customerField: 'supplierDeliveryMode' },
    ];
  }
  getEntityCallback(): void {
    super.getEntityCallback();

    this.loadAddresses(this.entity.thirdPartyId);
  }

  protected loadAddresses(thirdPartyId: string) {
    this.thirdPartyDirectDeliveryAddress$ = this.supplierService
      .addressList(thirdPartyId)
      .pipe(
        map(obs =>
          obs.filter(address => address.roles.some(role => role == AddressRole.DirectDelivery)),
        ),
      );

    this.thirdPartyPickUpAddress$ = this.supplierService
      .addressList(thirdPartyId)
      .pipe(
        map(obs => obs.filter(address => address.roles.some(role => role == AddressRole.PickUp))),
      );

    this.supplierService.getSupplierMainAddress(thirdPartyId).subscribe(address => {
      this.thirdPartyAddress = address;
    });

    this.subscriptionService.addOne(this.thirdPartyContacts$, contacts => {
      const contactControl = this.form.get('thirdPartyContactId');
      if (contactControl?.value != null && !contacts.some(x => x.id === contactControl.value)) {
        contactControl.setValue(null);
      }
    });
  }

  setDeliveryAddress() {
    const companySiteId = this.form.get('companySiteId').value;
    const deliveryMode = this.form.get('supplierDeliveryModeId').value;
    const deliveryAddressIdSelected = this.form.get('deliveryAddressIdSelected').value;

    const deliveryAddressFormControl = this.form.get('deliveryAddress');
    this.disableAddressComponent(deliveryAddressFormControl);

    switch (deliveryMode) {
      case this.deliveryMode.OnSite:
        if (companySiteId != null) {
          this.setCompanySiteAddress(companySiteId);
        }
        break;
      case this.deliveryMode.OtherSite:
        this.companySites = this.companySiteStore
          .entities()
          .filter(company => company.id != this.form.get('companySiteId').value);
        if (deliveryAddressIdSelected != null) {
          this.setCompanySiteAddress(deliveryAddressIdSelected);
        }
        break;
      case this.deliveryMode.DirectDelivery:
        this.deliveryAddress$ = this.thirdPartyDirectDeliveryAddress$;

        if (deliveryAddressIdSelected == null) {
          this.enableAddressComponent(deliveryAddressFormControl);
        } else {
          this.thirdPartyDirectDeliveryAddress$
            .pipe(map(obs => obs.filter(address => address.id == deliveryAddressIdSelected)))
            .subscribe(address => {
              this.deliveryAddressSelected$.next(address[0]);
            });
        }
        break;
      case this.deliveryMode.PickUp:
        this.deliveryAddress$ = this.thirdPartyPickUpAddress$;

        if (deliveryAddressIdSelected == null) {
          this.deliveryAddressSelected$.next(this.thirdPartyAddress);
          this.enableAddressComponent(deliveryAddressFormControl);
        } else {
          this.thirdPartyPickUpAddress$
            .pipe(map(obs => obs.filter(address => address.id == deliveryAddressIdSelected)))
            .subscribe(address => {
              this.deliveryAddressSelected$.next(address[0]);
            });
        }
        break;
    }

    this.deliveryAddressSelected$.subscribe(deliveryAddressSelected => {
      if (deliveryAddressSelected != null) {
        if (
          deliveryMode == this.deliveryMode.DirectDelivery ||
          (deliveryMode == this.deliveryMode.PickUp && deliveryAddressIdSelected != null)
        ) {
          let thirdPartyAddressControl = deliveryAddressFormControl.get('thirdPartyAddressId');
          if (thirdPartyAddressControl?.value != deliveryAddressSelected?.id) {
            thirdPartyAddressControl.setValue(deliveryAddressSelected?.id);
          }
        }

        deliveryAddressFormControl
          .get('street')
          .setValue(deliveryAddressSelected.street, { emitEvent: false });
        deliveryAddressFormControl
          .get('address2')
          .setValue(deliveryAddressSelected.address2, { emitEvent: false });
        deliveryAddressFormControl
          .get('countryId')
          .setValue(deliveryAddressSelected.countryId, { emitEvent: false });
        deliveryAddressFormControl
          .get('localityId')
          .setValue(deliveryAddressSelected.localityId, { emitEvent: false });
        deliveryAddressFormControl
          .get('latitude')
          .setValue(deliveryAddressSelected.latitude, { emitEvent: false });
        deliveryAddressFormControl
          .get('longitude')
          .setValue(deliveryAddressSelected.longitude, { emitEvent: false });
      }
    });
  }

  protected disableAddressComponent(deliveryAddressFormControl: AbstractControl<any, any>) {
    deliveryAddressFormControl.get('thirdPartyAddressId').disable();
    deliveryAddressFormControl.get('street').disable();
    deliveryAddressFormControl.get('address2').disable();
    deliveryAddressFormControl.get('countryId').disable();
    deliveryAddressFormControl.get('localityId').disable();
    deliveryAddressFormControl.get('latitude').disable();
    deliveryAddressFormControl.get('longitude').disable();
    this.showSearch = false;
  }

  protected enableAddressComponent(deliveryAddressFormControl: AbstractControl<any, any>) {
    deliveryAddressFormControl.get('thirdPartyAddressId').enable();
    deliveryAddressFormControl.get('street').enable();
    deliveryAddressFormControl.get('address2').enable();
    deliveryAddressFormControl.get('countryId').enable();
    deliveryAddressFormControl.get('localityId').enable();
    deliveryAddressFormControl.get('latitude').enable();
    deliveryAddressFormControl.get('longitude').enable();
    this.showSearch = true;
  }

  protected setEmptyAddressComponent() {
    const deliveryAddressFormControl = this.form.get('deliveryAddress');
    deliveryAddressFormControl.get('thirdPartyAddressId').setValue(null);
    deliveryAddressFormControl.get('street').setValue(null);
    deliveryAddressFormControl.get('address2').setValue(null);
    deliveryAddressFormControl.get('countryId').setValue(null);
    deliveryAddressFormControl.get('localityId').setValue(null);
    deliveryAddressFormControl.get('latitude').setValue(null);
    deliveryAddressFormControl.get('longitude').setValue(null);
    this.deliveryAddressSelected$.next(null);
  }

  protected setCompanySiteAddress(companySiteId: string) {
    this.companySite$
      .pipe(
        map(obs => obs.find(companySite => companySite.id == companySiteId).address),
        map(companySiteAddress => {
          return [
            {
              isActive: true,
              name: companySiteAddress.name,
              email: companySiteAddress.email,
              phone: companySiteAddress.phone,
              mobilePhone: null,
              street: companySiteAddress.street,
              address2: companySiteAddress.address2,
              longitude: null,
              latitude: null,
              note: companySiteAddress.note,
              openingTime: null,
              countryId: companySiteAddress.countryId,
              country: companySiteAddress.country,
              localityId: companySiteAddress.localityId,
              locality: companySiteAddress.locality,
              roles: companySiteAddress.rolesIds,
            },
          ] as AddressDto[];
        }),
      )
      .subscribe(address => {
        this.deliveryAddressSelected$.next(address[0]);
      });
  }

  override copy() {
    const modalRef = this.modalService.open(DialogBoxCloneComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      scrollable: true,
      injector: this.injector,
      size: 'l',
    });
    modalRef.componentInstance.documentId = this.entity.id;
    modalRef.componentInstance.docService = this.docService;
    this.annexes$.subscribe(x => {
      modalRef.componentInstance.areAnnexesIncluded = x.length != 0;
    });
  }

  override saveRequest(): Observable<TDocDto> {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.form.disabled) return;

    const deliveryAddressFormValue = this.form.get('deliveryAddress');
    const formValue = this.form.value;

    formValue.deliveryAddress_ThirdPartyAddressId = null;
    formValue.deliveryAddress_Address1 = deliveryAddressFormValue.get('street').value;
    formValue.deliveryAddress_LocalityId = deliveryAddressFormValue.get('localityId').value;
    formValue.deliveryAddress_CountryId = deliveryAddressFormValue.get('countryId').value;
    formValue.deliveryAddress_Address2 = deliveryAddressFormValue.get('address2').value;
    formValue.deliveryAddress_ContactInfo = deliveryAddressFormValue.get('contactInfo').value;
    formValue.deliveryAddress_Latitude = deliveryAddressFormValue.get('latitude').value;
    formValue.deliveryAddress_Longitude = deliveryAddressFormValue.get('longitude').value;

    const request = this.entity.id
      ? this.docService.update(this.entity.id, formValue)
      : this.docService.create(formValue);

    return request as Observable<TDocDto>;
  }

  protected override resetDeliveryAddressForm(options?: Object) {
    this.setDeliveryAddress();
    const deliveryAddressForm = this.form.get('deliveryAddress');

    deliveryAddressForm
      .get('thirdPartyAddressId')
      .setValue(this.entity.deliveryAddressIdSelected, options);
    deliveryAddressForm.get('street').setValue(this.entity.deliveryAddress_Address1, options);
    deliveryAddressForm.get('localityId').setValue(this.entity.deliveryAddress_LocalityId, options);
    deliveryAddressForm.get('countryId').setValue(this.entity.deliveryAddress_CountryId, options);
    deliveryAddressForm.get('address2').setValue(this.entity.deliveryAddress_Address2, options);
    deliveryAddressForm
      .get('contactInfo')
      .setValue(this.entity.deliveryAddress_ContactInfo, options);
    deliveryAddressForm.get('latitude').setValue(this.entity.deliveryAddress_Latitude, options);
    deliveryAddressForm.get('longitude').setValue(this.entity.deliveryAddress_Longitude, options);
  }
}
